import {ChangeDetectorRef, Component, OnInit, ViewChild} from '@angular/core';
import {MatSlideToggle} from '@angular/material/slide-toggle';
import {ApiResponseService} from 'src/app/services/api-response.service';
import {GlobalService} from 'src/app/services/global.service';
import {MfaEnableModal, MfaMethodType} from '../mfa-enable/mfa-enable.modal';

@Component({
    selector: 'app-mfa-status',
    templateUrl: './mfa-status.component.html',
    styleUrls: ['./mfa-status.component.scss'],
})
export class MfaStatusComponent implements OnInit {
    @ViewChild('phoneToggle') phoneToggle: MatSlideToggle;
    @ViewChild('emailToggle') emailToggle: MatSlideToggle;

    phoneEnabled = false;
    emailEnabled = false;
    phoneMfaId: string | null = null;
    emailMfaId: string | null = null;
    phoneLoading = false;
    emailLoading = false;

    constructor(
        private apiResponseService: ApiResponseService,
        private global: GlobalService,
        private cdr: ChangeDetectorRef
    ) {}

    get hasAnyEnabled(): boolean {
        return this.phoneEnabled || this.emailEnabled;
    }

    get canAddAnotherMethod(): boolean {
        return !this.phoneEnabled || !this.emailEnabled;
    }

    ngOnInit(): void {
        this.checkMfaStatus();
    }

    checkMfaStatus(): void {
        this.apiResponseService.getMfaStatus().subscribe((response) => {
            const mfaData = response?.data || [];
            this.phoneMfaId = null;
            this.emailMfaId = null;
            this.phoneEnabled = false;
            this.emailEnabled = false;

            mfaData.forEach((item: any) => {
                if (item?.isEnabled !== 'true') return;
                if (item?.name === 'phoneNumber') {
                    this.phoneMfaId = item.mfaId;
                    this.phoneEnabled = true;
                } else if (item?.name === 'email') {
                    this.emailMfaId = item.mfaId;
                    this.emailEnabled = true;
                }
            });
            this.cdr.detectChanges();
        });
    }

    async togglePhone(event: {checked: boolean}): Promise<void> {
        if (event.checked) {
            await this.enableMfaMethod('phoneNumber');
        } else {
            await this.disableMfaMethod('phoneNumber');
        }
    }

    async toggleEmail(event: {checked: boolean}): Promise<void> {
        if (event.checked) {
            await this.enableMfaMethod('email');
        } else {
            await this.disableMfaMethod('email');
        }
    }

    /**
     * Open modal to enable a single MFA method. User must verify via OTP before the method is enabled.
     */
    async enableMfaMethod(method: MfaMethodType): Promise<void> {
        const modal = await this.global.modalCtrl.create({
            component: MfaEnableModal,
            cssClass: 'auto-height ion-disable-focus-trap',
            componentProps: {preselectedType: method},
        });
        modal.onDidDismiss().then((detail) => {
            if (detail.data?.verified && detail.data?.type === method) {
                this.global.showLoading();
                if (method === 'phoneNumber') {
                    this.startPhoneNumberMFA();
                } else {
                    this.startEmailMFA();
                }
            } else {
                this.syncToggleState(method, false);
            }
        });
        await modal.present();
    }

    async addAnotherMfaMethod(): Promise<void> {
        const enabledMethods: MfaMethodType[] = [];
        if (this.phoneEnabled) enabledMethods.push('phoneNumber');
        if (this.emailEnabled) enabledMethods.push('email');

        const modal = await this.global.modalCtrl.create({
            component: MfaEnableModal,
            cssClass: 'auto-height ion-disable-focus-trap',
            componentProps: {enabledMethods},
        });
        modal.onDidDismiss().then((detail) => {
            if (detail.data?.verified) {
                const type = detail.data?.type as MfaMethodType | 'both';
                this.global.showLoading();
                if (type === 'both') {
                    this.startPhoneNumberMFA();
                    this.startEmailMFA();
                } else if (type === 'phoneNumber') {
                    this.startPhoneNumberMFA();
                } else if (type === 'email') {
                    this.startEmailMFA();
                }
            }
        });
        await modal.present();
    }

    async disableMfaMethod(method: MfaMethodType): Promise<void> {
        const mfaId = method === 'phoneNumber' ? this.phoneMfaId : this.emailMfaId;
        if (!mfaId) {
            this.syncToggleState(method, false);
            return;
        }
        const prompt = await this.global.alertCtrl.create({
            header: this.global.translate.instant('label.warning'),
            message: this.global.translate.instant('label.mfa_disable_confirm'),
            buttons: [
                {
                    text: this.global.translate.instant('label.no'),
                    handler: () => this.syncToggleState(method, true),
                },
                {
                    text: this.global.translate.instant('label.yes'),
                    handler: () => this.doDisableMfa(method, mfaId),
                },
            ],
        });
        await prompt.present();
    }

    private doDisableMfa(method: MfaMethodType, mfaId: string): void {
        if (method === 'phoneNumber') this.phoneLoading = true;
        else this.emailLoading = true;
        this.cdr.detectChanges();

        this.apiResponseService.deleteMfa(mfaId).subscribe({
            next: (response) => {
                if (response?.message === 'success') {
                    if (method === 'phoneNumber') {
                        this.phoneMfaId = null;
                        this.phoneEnabled = false;
                    } else {
                        this.emailMfaId = null;
                        this.emailEnabled = false;
                    }
                    this.global.basicAlert('', this.global.translate.instant('label.saved_successfully'));
                } else {
                    this.syncToggleState(method, true);
                }
                this.checkMfaStatus();
            },
            error: () => this.syncToggleState(method, true),
            complete: () => {
                this.phoneLoading = false;
                this.emailLoading = false;
                this.cdr.detectChanges();
            },
        });
    }

    private syncToggleState(method: MfaMethodType, checked: boolean): void {
        if (method === 'phoneNumber') {
            this.phoneEnabled = checked;
            if (this.phoneToggle) this.phoneToggle.checked = checked;
        } else {
            this.emailEnabled = checked;
            if (this.emailToggle) this.emailToggle.checked = checked;
        }
        this.cdr.detectChanges();
    }

    startPhoneNumberMFA(): void {
        this.apiResponseService.postMfaStatus(this.global.isMobileApplication, 'phoneNumber').subscribe({
            next: (response) => {
                if (response?.message === 'success') {
                    this.global.setLocalStorage('tenantpayToken', response?.data?.loginToken);
                    this.global.initLoggedInUser(true);
                }
                this.checkMfaStatus();
            },
            error: () => this.checkMfaStatus(),
            complete: () => this.global.hideLoading(),
        });
    }

    startEmailMFA(): void {
        this.apiResponseService.postMfaStatus(this.global.isMobileApplication, 'email').subscribe({
            next: (response) => {
                if (response?.message === 'success') {
                    this.global.setLocalStorage('tenantpayToken', response?.data?.loginToken);
                    this.global.initLoggedInUser(true);
                }
                this.checkMfaStatus();
            },
            error: () => this.checkMfaStatus(),
            complete: () => this.global.hideLoading(),
        });
    }
}
